// Field knowledge for the member screen: _User and the profile classes it
// points at (UserInfo_v3, Settings, UserTraining, ScheduleDays).
import { toDate } from '../Shared/detailFormat'

const LB_PER_KG = 2.20462

// Settings.yearBorn + monthBorn (1-12) are the only age source; the day is
// never stored. Without the month, or in the birth month itself, the age is
// one of two values.
export const describeAge = (yearBorn, monthBorn, now = new Date()) => {
  if (typeof yearBorn !== 'number') {
    return null
  }
  const older = now.getFullYear() - yearBorn
  const month = now.getMonth() + 1
  if (typeof monthBorn !== 'number' || monthBorn === month) {
    return `${older - 1}–${older}`
  }
  return String(monthBorn < month ? older : older - 1)
}

// Settings.weight stores kilograms plus the unit the member reads it in.
export const describeWeight = (kilograms, displayUnit) => {
  if (typeof kilograms !== 'number') {
    return null
  }
  if (displayUnit === 'lb') {
    return `${Math.round(kilograms * LB_PER_KG)} lb (${kilograms} kg)`
  }
  return `${kilograms} kg`
}

const WEEKDAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
]

// ScheduleDays keeps one { active } object per weekday it has ever stored.
export const activeSwimDays = (scheduleDays) => {
  if (!scheduleDays) {
    return []
  }
  return WEEKDAYS
    .filter((day) => scheduleDays.get(day)?.active === true)
    .map((day) => day.charAt(0).toUpperCase() + day.slice(1, 3))
}

// fieldHistory is an audit list kept on _User, UserInfo_v3 and Settings.
// Merged newest first so a support question ("when did their pool change?")
// is answered by one table.
export const mergeFieldHistory = (sources) => {
  const rows = []
  sources.forEach(({ className, object }) => {
    ;(object?.get('fieldHistory') || []).forEach((entry, index) => {
      if (!entry) {
        return
      }
      rows.push({
        key: `${className}-${index}`,
        className,
        field: entry.field,
        oldValue: entry.oldValue,
        newValue: entry.newValue,
        timestamp: toDate(entry.timestamp)
      })
    })
  })
  return rows.sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0))
}

// yearlyTotals_v9 is keyed by year; newest year first.
export const yearlyTotalsRows = (yearlyTotals) =>
  Object.entries(yearlyTotals || {})
    .filter(([, totals]) => totals && typeof totals === 'object')
    .map(([year, totals]) => ({ year, ...totals }))
    .sort((a, b) => Number(b.year) - Number(a.year))

// _User.skillLevel (deprecated; only a fallback workout length) and
// Settings.strengthLevel share this enum.
export const SKILL_LEVEL_LABELS = {
  0: 'Novice',
  1: 'Beginner',
  2: 'Intermediate',
  3: 'Advanced',
  4: 'Pro'
}

export const TRAINING_FOCUS_LABELS = { 1: 'Swim', 2: 'Dryland', 3: 'Both' }

export const describeEnum = (labels, value) =>
  value === undefined || value === null
    ? null
    : `${labels[value] || 'Unknown'} (${value})`

// Settings saves a false boolean by unsetting it, so on a loaded Settings row
// "absent" means No.
export const describeSettingsFlag = (settings, key) => {
  if (!settings) {
    return null
  }
  return settings.get(key) ? 'Yes' : 'No'
}

// UserTraining.activePlans maps a template id, or 'generated-<epoch ms>' for
// an AI-generated plan, to the member's own TrainingPlan copy.
const generatedPlanTime = (key) => {
  const match = /^generated-(\d+)$/.exec(key || '')
  return match ? Number(match[1]) : null
}

export const describePlanKey = (key) => {
  const time = generatedPlanTime(key)
  if (time === null) {
    return key
  }
  const date = new Date(time)
  return isNaN(date.getTime()) ? key : 'Generated ' + date.toLocaleDateString()
}

// activePlans gains a key per generated plan and is never pruned, so it is
// ordered for a short list: the plans the member is on now, then generated
// plans newest first, then template copies.
export const activePlanRows = (activePlans, currentPlanIds) => {
  const current = new Set((currentPlanIds || []).filter(Boolean))
  return Object.entries(activePlans || {})
    .map(([key, planId]) => ({
      key,
      planId,
      isCurrent: current.has(planId),
      generatedAt: generatedPlanTime(key)
    }))
    .sort((a, b) =>
      (Number(b.isCurrent) - Number(a.isCurrent)) ||
      ((b.generatedAt ?? -1) - (a.generatedAt ?? -1)) ||
      a.key.localeCompare(b.key))
}

// Fields the apps no longer write (or superseded versions of current ones),
// listed so the member screen can show them in a collapsed "Legacy" block.
export const USER_LEGACY_FIELDS = [
  ['sex', 'Sex (now Settings.gender)'],
  ['dob', 'Date of birth (retired)'],
  ['displayName', 'Display name (stale)'],
  ['profilePic', 'Profile pic'],
  ['firstName', 'First name on _User'],
  ['lastName', 'Last name on _User'],
  ['gohighlevelContactId', 'GoHighLevel contact'],
  ['lastSavedAccountDataUrl', 'Account export URL'],
  ['lastSavedAccountData', 'Account export'],
  ['timeSwum', 'Time swum'],
  ['distanceSwum', 'Distance swum'],
  ['caloriesBurned', 'Calories burned'],
  ['version', 'Version'],
  ['swolfFreestyleSum', 'SWOLF sum'],
  ['swolfFreestyleCount', 'SWOLF count'],
  ['totalFree', 'Total free'],
  ['totalBack', 'Total back'],
  ['totalBreast', 'Total breast'],
  ['totalFly', 'Total fly'],
  ['totalKick', 'Total kick'],
  ['totalIM', 'Total IM'],
  ['workoutsCompleted', 'Workouts completed'],
  ['workoutsCompletedDates', 'Workout dates'],
  ['totalDistanceSwamMeters', 'Total distance (m)'],
  ['totalTimeSwamSeconds', 'Total time (s)'],
  ['totalCompletedWorkouts', 'Total workouts'],
  ['purchasedProductId', 'Purchased product'],
  ['purchasedProductStatus', 'Purchase status']
]

export const USER_POOL_LEGACY_FIELDS = [
  ['preferredPools', 'Preferred pools'],
  ['customPoolDistance', 'Custom pool distance'],
  ['customPoolUnits', 'Custom pool units (0 yd, 1 m)'],
  ['customPoolUnit', 'Custom pool unit'],
  ['poolCourse', 'Pool course (0 SCY, 1 SCM, 2 LCM)'],
  ['preferredPoolLength', 'Preferred pool length'],
  ['preferredPoolLengthUnit', 'Preferred pool length unit']
]

export const USER_SUBSCRIPTION_LEGACY_FIELDS = [
  ['subscription', 'Subscription row'],
  ['subscription_v4', 'Subscription_v4 row']
]

export const USER_INFO_PROFILE_LEGACY_FIELDS = [
  ['displayName', 'Display name'],
  ['isSuggestedAthlete', 'Suggested athlete'],
  ['currentTrainingPlan', 'Current training plan'],
  ['trainingGoals', 'Training goals (deprecated 2020)']
]

export const USER_INFO_STATS_LEGACY_FIELDS = [
  ['totalDistanceByStroke', 'Distance by stroke (stale)'],
  ['testSetsPassed', 'Test sets passed (no writer)'],
  ['workoutStreakWeeks', 'Streak weeks'],
  ...[1, 2, 3, 5].flatMap((version) => [
    [`workoutStreakCachedWeeks_v${version}`, `Cached streak v${version}`],
    [`workoutStreakCachedLastWorkoutDate_v${version}`, `Cached streak date v${version}`]
  ]),
  ...[1, 2, 3, 4].flatMap((version) => [
    [`swolfFreestyleSum_v${version}`, `SWOLF sum v${version}`],
    [`swolfFreestyleCount_v${version}`, `SWOLF count v${version}`]
  ]),
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((version) =>
    [`yearlyTotals_v${version}`, `Yearly totals v${version}`])
]

export const SETTINGS_LEGACY_FIELDS = [
  ['isMigratedToScheduledWorkouts', 'Migrated to scheduled workouts'],
  ['nonsubscriberWeekEmailSent', 'Non-subscriber week email'],
  ['subscriberUnguidedPushSent', 'Subscriber unguided push'],
  ['userSwimdaysPushSent', 'Swim days push'],
  ['skillLevelSwim', 'Swim skill level (old)'],
  ['skillLevelDryland', 'Dryland skill level (old)']
]

export const TRAINING_LEGACY_FIELDS = [
  ['isCopiedFromOldSchema', 'Copied from old schema']
]

const legacyKeys = (...lists) => lists.flat().map(([key]) => key)

// Keys each structured section renders, for the "All fields" panels.
export const USER_SHOWN_FIELDS = [
  'username', 'email', 'phoneNumber', 'isTransactionalEmailsUnsubscribed',
  'isAdTrackingDisabled', 'lastPasswordResetLink', 'acquisitionChannel',
  'lastAmplitudeSessionId', 'clickfunnelsContactId', 'clickfunnelsPublicId',
  'clickfunnels_public_id', 'didSendFirstWorkoutAnalysisEmail',
  'lastSavedAccountDataKey', 'garminAccessToken', 'garminTokenSecret',
  'garminOAuth2AccessToken', 'garminOAuth2RefreshToken',
  'garminOAuth2ExpiresAt', 'garminUserId', 'garminLinkedAt',
  'garminLastPushAt', 'garminTokenExpiredAt', 'stravaAccessToken',
  'stravaRefreshToken', 'trainingPeaksAccessToken',
  'trainingPeaksRefreshToken', 'sendbirdChannelId', 'pools', 'skillLevel',
  'preferredWorkoutLength', 'onboardingCallDate', 'coachCallDate',
  'settings', 'userInfo_v3', 'userTraining', 'fieldHistory', 'tempWorkout',
  'loadedTempWorkout', 'bio',
  ...legacyKeys(USER_LEGACY_FIELDS, USER_POOL_LEGACY_FIELDS, USER_SUBSCRIPTION_LEGACY_FIELDS)
]

export const USER_INFO_SHOWN_FIELDS = [
  'parentUser', 'userId', 'firstName', 'lastName', 'loginMethod', 'bio',
  'profilePicture', 'private', 'followers', 'following', 'facebookId',
  'totalWorkoutsCompleted', 'totalDistance', 'totalTime', 'longestStreak',
  'swolfFreestyleSum_v5', 'swolfFreestyleCount_v5',
  'workoutStreakCachedWeeks_v4', 'workoutStreakCachedLastWorkoutDate_v4',
  'yearlyTotals_v9', 'testSetDismissedCount', 'trainingPlanHistory',
  'wodTrialStart', 'adminNotes', 'times', 'fieldHistory',
  ...legacyKeys(USER_INFO_PROFILE_LEGACY_FIELDS, USER_INFO_STATS_LEGACY_FIELDS)
]

export const SETTINGS_SHOWN_FIELDS = [
  'userId', 'swimDays', 'yearBorn', 'monthBorn', 'gender', 'weight',
  'heartRateZones', 'injuries', 'waterConfidence', 'swimSkillLevel',
  'strengthLevel', 'trainingFocus', 'goalsSwim', 'goalsDryland',
  'swimStrokes', 'equipment', 'isNotificationOn', 'isPrivate', 'hasEquipment',
  'trainingPlanWeeklyEmailEnabled', 'isFirstWorkoutConfigurationSkipped',
  'secondsFromGMT', 'appleWatchConfiguration', 'legacyGarminCompatibility',
  'garminKeepLastRestStep', 'fieldHistory',
  ...legacyKeys(SETTINGS_LEGACY_FIELDS)
]

export const TRAINING_SHOWN_FIELDS = [
  'userId', 'activePlans', 'activeSwimPlanId', 'activeStrengthPlanId',
  ...legacyKeys(TRAINING_LEGACY_FIELDS)
]

export const SCHEDULE_DAYS_SHOWN_FIELDS = ['userId', 'type', ...WEEKDAYS]
